import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  PageHeaderComponent,
  PageBodyComponent,
  DateFieldComponent,
  FilledButtonComponent,
  OutlinedButtonComponent,
  SearchInputComponent,
  ButtonToggleComponent,
  TableComponent,
  NotificationContainerComponent,
  ExportJobCardState,
} from '@custom-components';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { CardComponent } from '@custom-components/general/card/card.component';
import { LANGUAGE_CODE_SUPPORTED } from '@core/constants/language.constant';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import { DeviceFiltersModel, LocationModel, LocationPayloadModel } from '@core/models';
import {
  DeviceService,
  InstallationGroupService,
  LocationService,
  ExportNotificationService,
} from '@core/services';
import { DeviceStore } from '@root/app/store/device.store';
import { InstallationGroupStore } from '@root/app/store/installation-group.store';
import { ExportJobStore } from '@root/app/store/export-job.store';
import {
  CreateExportJobRequest,
  EXPORT_GRANULARITIES,
  ExportGranularity,
  ExportJobStatus,
  InsightsService,
} from '@core/services/insights.service';
import { gunzipCsvUtil, parseContentDispositionFilename } from '@core/utils/gunzip-csv.util';
import {
  DeviceSelectionDialogComponent,
  DeviceSelectionDialogData,
} from '../../components/device-selection-dialog/device-selection-dialog.component';

/** Maps the active UI language to a BCP-47 locale for display-date formatting. */
const DISPLAY_DATE_LOCALE: Record<LANGUAGE_CODE_SUPPORTED, string> = {
  [LANGUAGE_CODE_SUPPORTED.en]: 'en-GB',
  [LANGUAGE_CODE_SUPPORTED.nl]: 'nl-NL',
};

/**
 * Maximum export range: 3 years, regardless of granularity.
 */
const MAX_EXPORT_RANGE_DAYS = 3 * 366;

/** Data points produced per device per day at each granularity, used to size the large-export warning. */
const POINTS_PER_DAY: Record<ExportGranularity, number> = {
  quarter_hour: 96,
  hour: 24,
  day: 1,
  month: 1 / 30,
  year: 1 / 365,
};

/** Fetched once per selected group so the full membership resolves in a single call. */
const GROUP_MEMBER_FETCH_LIMIT = 1000;

/** Matches the limit used everywhere else this endpoint is called (e.g. add-installation-group-dialog,
 *  create-partnership-dialog) — the backend caps/rejects larger page sizes for the groups list itself. */
const GROUP_LIST_FETCH_LIMIT = 100;

/** No pagination UI yet — this just bounds how much history a single fetch/poll returns. */
const HISTORY_PAGE_SIZE = 20;

/** Only three statuses are shown to the user: busy (covers both queued and running),
 *  failed, or ready/green once the file can be downloaded. */
const HISTORY_STATUS_KEYS: Record<ExportJobStatus, string> = {
  queued: 'dataExport.history.statusRunning',
  running: 'dataExport.history.statusRunning',
  completed: 'dataExport.history.statusReady',
  failed: 'dataExport.history.statusFailed',
};

const HISTORY_STATUS_STATES: Record<ExportJobStatus, 'success' | 'warning' | 'error' | 'neutral'> =
  {
    queued: 'warning',
    running: 'warning',
    completed: 'success',
    failed: 'error',
  };

interface DeviceChip {
  id: number;
  label: string;
}

interface GroupChip {
  oid: string;
  name: string;
  deviceIds: Set<number>;
}

interface LocationChip {
  id: number;
  label: string;
  deviceIds: Set<number>;
}

/** Fetched once per selected location so the full device list resolves in a single call. */
const LOCATION_DEVICE_FETCH_LIMIT = 1000;

/** How many address matches to show per keystroke in the unified search dropdown. */
const LOCATION_SEARCH_RESULT_LIMIT = 10;

type ExportHistoryRow = Record<string, unknown> & {
  exportJobOid: string;
  devicesLabel: string;
  periodLabel: string;
  granularityLabel: string;
  dataLabel: string;
  createdAtLabel: string;
  statusLabel: string;
  statusState: 'success' | 'warning' | 'error' | 'neutral';
  downloadAvailable: boolean;
  downloadUrl?: string;
  fileName?: string;
};

// Widths intentionally sum to ~88%, leaving room for the trailing (unwidthed) actions column
// so the download button stays visible instead of being squeezed to zero width.
const HISTORY_COLUMNS: TableColumnConfig[] = [
  { field: 'devicesLabel', label: 'dataExport.history.devices', width: '20%', bold: true },
  { field: 'periodLabel', label: 'dataExport.history.period', width: '18%' },
  { field: 'granularityLabel', label: 'dataExport.history.granularity', width: '12%' },
  { field: 'dataLabel', label: 'dataExport.history.data', width: '16%' },
  { field: 'createdAtLabel', label: 'dataExport.history.created', width: '12%' },
  {
    field: 'statusLabel',
    label: 'dataExport.history.status',
    width: '10%',
    type: TableColumnType.Tag,
    stateField: 'statusState',
  },
];

const ENERGY_PRESETS = {
  ALL: 'ALL',
  FORWARD: 'FORWARD',
  BACKWARD: 'BACKWARD',
  FORWARD_BACKWARD: 'FORWARD_BACKWARD',
} as const;

type EnergyPreset = (typeof ENERGY_PRESETS)[keyof typeof ENERGY_PRESETS];

/** Translation keys for each preset, reused by both the picker and the history "Data" column. */
const PRESET_LABEL_KEYS: Record<EnergyPreset, string> = {
  ALL: 'dataExport.energyPresets.all',
  FORWARD: 'dataExport.energyPresets.forward',
  BACKWARD: 'dataExport.energyPresets.backward',
  FORWARD_BACKWARD: 'dataExport.energyPresets.forwardBackward',
};

@Component({
  selector: 'xemex-data-export-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    TranslateModule,
    PageHeaderComponent,
    PageBodyComponent,
    DateFieldComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    SearchInputComponent,
    ButtonToggleComponent,
    CardComponent,
    TableComponent,
    NotificationContainerComponent,
  ],
  providers: [DeviceStore, ExportJobStore, { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' }],
  templateUrl: './data-export-page.component.html',
  styleUrl: './data-export-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataExportPageComponent {
  private readonly translate = inject(TranslateService);
  private readonly insightsService = inject(InsightsService);
  private readonly installationGroupService = inject(InstallationGroupService);
  private readonly installationGroupStore = inject(InstallationGroupStore);
  private readonly locationService = inject(LocationService);
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly notificationService = inject(ExportNotificationService);
  private readonly dialog = inject(MatDialog);
  readonly deviceStore = inject(DeviceStore);
  readonly exportJobStore = inject(ExportJobStore);

  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    { label: this.translate.instant('sidebarMenu.dataExport') },
  ];

  // --- Location/group search + chips ---

  protected readonly searchTerm = signal('');
  protected readonly isSearchOpen = signal(false);
  private readonly searchInput$ = new Subject<string>();
  private readonly selectionCancel$ = new Subject<void>();
  protected readonly installationGroups = computed(
    () => this.installationGroupStore.data()?.installationGroups ?? [],
  );
  protected readonly locationResults = signal<LocationModel[]>([]);
  protected readonly groupResults = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return [];
    return this.installationGroups().filter(g => g.name.toLowerCase().includes(term));
  });

  protected readonly deviceChips = signal<DeviceChip[]>([]);
  protected readonly groupChips = signal<GroupChip[]>([]);
  protected readonly locationChips = signal<LocationChip[]>([]);
  protected readonly isResolvingSelection = signal(false);
  protected readonly isChipsExpanded = signal(false);

  protected readonly CHIPS_DISPLAY_LIMIT = 3;

  protected readonly allChips = computed(() => [
    ...this.deviceChips().map(c => ({ type: 'device' as const, chip: c })),
    ...this.locationChips().map(c => ({ type: 'location' as const, chip: c })),
    ...this.groupChips().map(c => ({ type: 'group' as const, chip: c })),
  ]);

  protected readonly visibleChips = computed(() => {
    const all = this.allChips();
    return this.isChipsExpanded() ? all : all.slice(0, this.CHIPS_DISPLAY_LIMIT);
  });

  protected readonly hasMoreChips = computed(
    () => this.allChips().length > this.CHIPS_DISPLAY_LIMIT,
  );

  /** Union of device ids from individually-picked devices, every selected group and location. */
  protected readonly selectedKeys = computed<Set<number>>(() => {
    const ids = new Set<number>(this.deviceChips().map(c => c.id));
    this.groupChips().forEach(g => g.deviceIds.forEach(id => ids.add(id)));
    this.locationChips().forEach(l => l.deviceIds.forEach(id => ids.add(id)));
    return ids;
  });
  protected readonly selectedCount = computed(() => this.selectedKeys().size);
  protected readonly hasNoSelection = computed(() => this.selectedKeys().size === 0);

  /** Latches true the first time the fleet is known to be non-empty; a search yielding zero
   *  results must not flip the page back to the "no devices at all" empty state. */
  private readonly hasAnyDevicesEver = signal(false);
  readonly hasDevices = computed(() => this.hasAnyDevicesEver());

  // --- Energy Presets ---
  protected readonly availablePresets = [
    { value: ENERGY_PRESETS.ALL, labelKey: 'dataExport.energyPresets.all' },
    { value: ENERGY_PRESETS.FORWARD, labelKey: 'dataExport.energyPresets.forward' },
    { value: ENERGY_PRESETS.BACKWARD, labelKey: 'dataExport.energyPresets.backward' },
    {
      value: ENERGY_PRESETS.FORWARD_BACKWARD,
      labelKey: 'dataExport.energyPresets.forwardBackward',
    },
  ];

  protected readonly selectedEnergyPreset = signal<EnergyPreset>('ALL');

  protected readonly selectEnergyPreset = (preset: EnergyPreset): void => {
    this.selectedEnergyPreset.set(preset);
  };

  // --- Granularity + period ---

  protected readonly granularityOptions = computed(() =>
    EXPORT_GRANULARITIES.map(g => ({ value: g.value, label: this.translate.instant(g.labelKey) })),
  );

  readonly maxDate = new Date();
  readonly exportForm = new FormGroup({
    startDate: new FormControl<Date | null>(this.defaultStart(), Validators.required),
    endDate: new FormControl<Date | null>(new Date(), Validators.required),
    granularity: new FormControl<ExportGranularity>('day', { nonNullable: true }),
  });

  readonly isSubmitting = signal(false);
  readonly submitError = signal<string | null>(null);
  protected readonly selectionError = signal<string | null>(null);

  // --- Export history (real jobs, polled while any are queued/running) ---
  protected readonly historyColumns = HISTORY_COLUMNS;
  protected readonly historyRows = signal<ExportHistoryRow[]>([]);
  protected readonly isLoadingHistory = signal(false);

  constructor() {
    this.deviceStore.getDevicesMethod(0);
    this.installationGroupStore.getInstallationGroups({
      limit: GROUP_LIST_FETCH_LIMIT,
      offset: 0,
    });
    this.exportJobStore.loadHistory(HISTORY_PAGE_SIZE, 0);

    effect(() => {
      const data = this.deviceStore.deviceData();
      if (data.numberOfItems > 0) this.hasAnyDevicesEver.set(true);
    });

    // Update history rows when jobs change in the store
    effect(() => {
      this.exportJobStore.completedJobs();
      this.exportJobStore.failedJobs();
      this.exportJobStore.activeJobs();
      this.updateHistoryRows();
    });

    // Debounced search input: wait 300ms after user stops typing before making API calls
    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          this.searchTerm.set(value);
          this.isSearchOpen.set(true);

          if (!value) {
            this.locationResults.set([]);
            return of(null);
          }

          return this.locationService.getListLocationsFilter({
            address: value,
            limit: LOCATION_SEARCH_RESULT_LIMIT,
            offset: 0,
          } as LocationPayloadModel);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: res => {
          if (res) {
            this.locationResults.set(res.locations ?? []);
          }
        },
        error: () => this.locationResults.set([]),
      });

    // Arriving from devices, locations, or installation-groups page ("Export data" on a selection)
    // pre-fills the selection with devices directly (from devices page) or every device in each
    // passed-in group/location, shown as removable chips. Passed via router state (not query params)
    // so the ids never appear in the URL.
    const deviceIds = (history.state?.deviceIds as (string | number)[] | undefined) ?? [];
    deviceIds
      .map(Number)
      .filter(id => !Number.isNaN(id))
      .forEach(id => {
        const device = this.deviceStore.deviceData()?.devices?.find(d => d.deviceId === String(id));
        if (device) {
          this.onDeviceOptionSelected({
            deviceId: String(id),
            deviceLDN: device.deviceLDN || `Device ${id}`,
          });
        }
      });

    // Only 0 or 1 group is supported (see onGroupSelected) — pre-fill with the first passed-in group.
    const groupOids = (history.state?.groupOids as string[] | undefined) ?? [];
    const firstGroupOid = groupOids.find(Boolean);
    if (firstGroupOid) this.onGroupSelected(firstGroupOid);

    const locationIds = (history.state?.locationIds as (string | number)[] | undefined) ?? [];
    locationIds
      .map(Number)
      .filter(id => !Number.isNaN(id))
      .forEach(id => this.onLocationSelected(id));
  }

  private defaultStart(): Date {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDisplayDate(date: Date): string {
    const locale =
      DISPLAY_DATE_LOCALE[this.translate.currentLang as LANGUAGE_CODE_SUPPORTED] ?? 'en-GB';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private calculateDayCount(startDate: Date, endDate: Date): number {
    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  // --- Search + device chips ---

  protected onSearchInputChange(value: string): void {
    this.searchInput$.next(value);
  }

  protected onSearchFocus(): void {
    if (this.searchTerm()) this.isSearchOpen.set(true);
  }

  protected onSearchBlur(): void {
    this.isSearchOpen.set(false);
  }

  protected onDeviceOptionSelected(device: { deviceId: string; deviceLDN: string }): void {
    const id = Number(device.deviceId);
    if (!this.deviceChips().some(c => c.id === id)) {
      this.deviceChips.update(chips => [...chips, { id, label: device.deviceLDN }]);
    }
    this.closeSearch();
  }

  protected removeDeviceChip(id: number): void {
    this.deviceChips.update(chips => chips.filter(c => c.id !== id));
  }

  protected openDeviceSelectionDialog(): void {
    const dialogRef = this.dialog.open<
      DeviceSelectionDialogComponent,
      DeviceSelectionDialogData,
      DeviceChip[]
    >(DeviceSelectionDialogComponent, { data: { initialSelection: this.deviceChips() } });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) this.deviceChips.set(result);
      });
  }

  private closeSearch(): void {
    this.searchTerm.set('');
    this.isSearchOpen.set(false);
    this.locationResults.set([]);
  }

  // --- Installation groups ---

  protected onGroupOptionSelected(groupOid: string): void {
    this.onGroupSelected(groupOid);
    this.closeSearch();
  }

  protected onGroupSelected(groupOid: string | null): void {
    if (!groupOid || this.groupChips().some(g => g.oid === groupOid)) return;
    // Clear previous group selection (only 0 or 1 group allowed)
    this.groupChips.set([]);
    this.isResolvingSelection.set(true);
    this.selectionError.set(null);
    this.installationGroupService
      .getInstallationGroupDevices(groupOid, GROUP_MEMBER_FETCH_LIMIT)
      .pipe(takeUntil(this.selectionCancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          const deviceIds = new Set<number>();
          for (const device of res.devices ?? []) {
            deviceIds.add(device.deviceId);
          }
          const name =
            this.installationGroups().find(g => g.installationGroupOid === groupOid)?.name ??
            groupOid;
          this.groupChips.set([{ oid: groupOid, name, deviceIds }]);
          this.isResolvingSelection.set(false);
        },
        error: () => {
          this.isResolvingSelection.set(false);
          this.selectionError.set(this.translate.instant('dataExport.selectionResolveError'));
        },
      });
  }

  protected removeGroupChip(_oid: string): void {
    // NOSONAR S1172 - Angular material event handler
    this.groupChips.set([]);
  }

  // --- Locations ---

  protected onLocationOptionSelected(location: LocationModel): void {
    this.onLocationSelected(location.locationId);
    this.closeSearch();
  }

  protected onLocationSelected(locationId: number): void {
    if (!locationId || this.locationChips().some(l => l.id === locationId)) return;
    // Clear previous location selection (only 0 or 1 location allowed)
    this.locationChips.set([]);
    this.isResolvingSelection.set(true);
    this.selectionError.set(null);
    forkJoin({
      detail: this.locationService.getLocationDetail(String(locationId)),
      devices: this.deviceService.getListDevicesFilter({
        locationId: String(locationId),
        limit: LOCATION_DEVICE_FETCH_LIMIT,
        offset: 0,
      } as DeviceFiltersModel),
    })
      .pipe(takeUntil(this.selectionCancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ detail, devices }) => {
          const deviceIds = new Set<number>((devices.devices ?? []).map(d => Number(d.deviceId)));
          const label = this.formatLocationLabel(detail);
          this.locationChips.set([{ id: locationId, label, deviceIds }]);
          this.isResolvingSelection.set(false);
        },
        error: () => {
          this.isResolvingSelection.set(false);
          this.selectionError.set(this.translate.instant('dataExport.selectionResolveError'));
        },
      });
  }

  protected removeLocationChip(_id: number): void {
    // NOSONAR S1172 - Angular material event handler
    this.locationChips.set([]);
  }

  protected clearAllSelection(): void {
    this.deviceChips.set([]);
    this.locationChips.set([]);
    this.groupChips.set([]);
    this.isResolvingSelection.set(false);
    this.selectionError.set(null);
    this.selectionCancel$.next();
  }

  private formatLocationLabel(location: LocationModel): string {
    return this.locationAddress(location) || `#${location.locationId}`;
  }

  protected locationAddress(location: LocationModel): string {
    return [location.locationStreet, location.locationStreetNumber, location.locationCity]
      .filter(v => v != null && v !== '')
      .join(' ');
  }

  // --- Period / granularity validation ---

  protected isRangeInvalid(): boolean {
    const { startDate, endDate } = this.exportForm.value;
    return !!startDate && !!endDate && startDate > endDate;
  }

  protected isRangeTooLong(): boolean {
    const { startDate, endDate } = this.exportForm.value;
    if (!startDate || !endDate || startDate > endDate) return false;
    const days = this.calculateDayCount(startDate, endDate);
    return days > this.maxRangeDaysForSelectedGranularity();
  }

  protected maxRangeDaysForSelectedGranularity(): number {
    return MAX_EXPORT_RANGE_DAYS;
  }

  protected maxRangeYearsForSelectedGranularity(): number {
    return Math.round(MAX_EXPORT_RANGE_DAYS / 366);
  }

  protected canExport(): boolean {
    return (
      this.exportForm.valid &&
      !this.isRangeInvalid() &&
      !this.isRangeTooLong() &&
      !this.isSubmitting() &&
      this.selectedKeys().size > 0
    );
  }

  // --- Export ---

  /** A single export job carrying the whole selection. The backend takes the union of
   *  `installation_group_oid`, `location_ids` and `device_ids` and dedupes it by device id
   *  server-side (models/export_job.py), so every device appears exactly once in one CSV.
   *  Group and location membership is resolved server-side at job-creation time (fresh, not a
   *  stale client snapshot), hence they travel as ids rather than expanded device lists.
   *
   *  Individually-picked devices already covered by the selected group or location are dropped
   *  from `device_ids` — they're exported via that container instead. The backend would dedupe
   *  them anyway; filtering here keeps the request honest about what the loose list adds.
   *
   *  Energy preset is passed to the backend for field filtering. */
  private buildJobRequests(): CreateExportJobRequest[] {
    const start = this.toIsoDate(this.exportForm.value.startDate!);
    const end = this.toIsoDate(this.exportForm.value.endDate!);
    const granularity = this.exportForm.value.granularity ?? 'day';
    const preset = this.selectedEnergyPreset();
    // "All data" (ALL) must return every channel, byte-for-byte like the single-device CSV export
    // (`/telemetry/export/csv` sends `fields: null` and NO `energy_preset`). Sending
    // `energy_preset: 'ALL'` alongside `fields: null` made the backend narrow the columns to
    // forward+reverse energy — dropping PV/solar/weather — so for ALL we omit the preset entirely.
    //
    // The narrower presets send `energy_preset` ALONE and must NOT include an explicit `fields`
    // list: the backend's `resolve_energy_preset_to_fields` only maps the preset to the
    // forward/reverse energy channels when `fields` is empty (models/export_job.py). Sending
    // heatpump `fields` alongside the preset made the backend ignore the preset and filter to
    // those heatpump-only channels, so a plain e-meter exported every row as "missing" (empty CSV).
    const fieldSelection: Pick<CreateExportJobRequest, 'energy_preset' | 'fields'> =
      preset === ENERGY_PRESETS.ALL ? { fields: null } : { energy_preset: preset };

    const group = this.groupChips()[0];
    const location = this.locationChips()[0];

    // Devices already resolved from the selected group/location are exported via that
    // container - drop them from the explicit list so no device is requested twice.
    const coveredByContainers = new Set<number>();
    group?.deviceIds.forEach(id => coveredByContainers.add(id));
    location?.deviceIds.forEach(id => coveredByContainers.add(id));
    const explicitDeviceIds = this.deviceChips()
      .map(chip => chip.id)
      .filter(id => !coveredByContainers.has(id));

    const request: CreateExportJobRequest = {
      granularity,
      start_date: start,
      end_date: end,
      ...fieldSelection,
    };
    if (group) request.installation_group_oid = group.oid;
    if (location) request.location_ids = [String(location.id)];
    if (explicitDeviceIds.length) request.device_ids = explicitDeviceIds.map(String);

    // Guarded by canExport(), but stay safe if nothing resolved to a selector.
    if (!request.installation_group_oid && !request.location_ids && !request.device_ids) {
      return [];
    }
    return [request];
  }

  onExport(): void {
    if (!this.canExport()) {
      this.exportForm.markAllAsTouched();
      return;
    }

    const requests = this.buildJobRequests();
    if (!requests.length) return;

    // Check for large datasets (>400k data points)
    const totalDataPoints = this.estimateTotalDataPoints();
    if (totalDataPoints > 400000) {
      const estDuration = this.estimateExportDuration();
      const warningId = 'warning-' + crypto.randomUUID();
      this.notificationService.add({
        jobOid: warningId,
        type: 'warning',
        title: this.translate.instant('dataExport.largeExport.title'),
        message: this.translate.instant('dataExport.largeExport.message', {
          duration: estDuration,
          points: (totalDataPoints / 1000000).toFixed(1),
        }),
        primaryAction: {
          label: this.translate.instant('dataExport.largeExport.proceed'),
          handler: async () => {
            this.submitExportRequests(requests);
          },
        },
        sticky: true,
        position: 'top-right',
      });
    } else {
      this.submitExportRequests(requests);
    }
  }

  private submitExportRequests(requests: CreateExportJobRequest[]): void {
    this.isSubmitting.set(true);
    this.submitError.set(null);
    const preset = this.selectedEnergyPreset();

    forkJoin(
      requests.map(request =>
        this.insightsService.createExportJob(request).pipe(
          catchError((error: unknown) => {
            this.submitError.update(existing => existing ?? this.extractErrorMessage(error));
            return of(null);
          }),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        this.isSubmitting.set(false);
        // Remember the chosen preset per created job so the history "Data" column can show it.
        results.forEach(result => {
          if (result?.export_job_oid)
            this.exportJobStore.rememberPreset(result.export_job_oid, preset);
        });
        if (results.some(Boolean)) {
          this.snackBar.open(this.translate.instant('dataExport.exportStarted'), undefined, {
            duration: 4000,
          });
          this.exportJobStore.loadHistory(HISTORY_PAGE_SIZE, 0);
        }
      });
  }

  private estimateTotalDataPoints(): number {
    const dayCount = this.calculateDayCount(
      this.exportForm.value.startDate!,
      this.exportForm.value.endDate!,
    );
    const granularity = this.exportForm.value.granularity ?? 'day';
    return Math.round(this.selectedKeys().size * dayCount * POINTS_PER_DAY[granularity]);
  }

  private estimateExportDuration(): string {
    const dayCount = this.calculateDayCount(
      this.exportForm.value.startDate!,
      this.exportForm.value.endDate!,
    );
    const deviceCount = this.selectedKeys().size;
    const estimatedMs = deviceCount * dayCount * 50;
    const estimatedSeconds = estimatedMs / 1000;

    if (estimatedSeconds > 3600) {
      const hours = Math.floor(estimatedSeconds / 3600);
      const mins = Math.floor((estimatedSeconds % 3600) / 60);
      return `~${hours}h ${mins}m`;
    } else if (estimatedSeconds > 60) {
      const mins = Math.floor(estimatedSeconds / 60);
      return `~${mins}m`;
    } else {
      return `~${Math.ceil(estimatedSeconds)}s`;
    }
  }

  private extractErrorMessage(error: unknown): string {
    // ApiService.formatErrors already unwraps HttpErrorResponse to its parsed `.error` body, so
    // what arrives here is the body object ({ detail, message }) — not the HttpErrorResponse.
    // Still handle a raw HttpErrorResponse in case an error ever reaches us without that unwrap.
    const body = (error instanceof HttpErrorResponse ? error.error : error) as
      | { detail?: string; message?: string }
      | null
      | undefined;
    const detail = body?.detail ?? body?.message;
    return detail ?? this.translate.instant('dataExport.exportSubmitError');
  }

  // --- Export history ---

  protected updateHistoryRows(): void {
    // Convert store jobs to history rows
    const allJobs = [
      ...this.exportJobStore.activeJobs(),
      ...this.exportJobStore.completedJobs(),
      ...this.exportJobStore.failedJobs(),
    ];

    // Get unique jobs and convert to history rows
    const uniqueJobs = Array.from(new Map(allJobs.map(job => [job.jobOid, job])).values());

    this.historyRows.set(uniqueJobs.map(job => this.toHistoryRowFromCard(job)));
  }

  private toHistoryRowFromCard(job: ExportJobCardState): ExportHistoryRow {
    return {
      exportJobOid: job.jobOid,
      devicesLabel: job.deviceLabel,
      periodLabel: `${this.formatDisplayDate(new Date(job.startDate))} - ${this.formatDisplayDate(new Date(job.endDate))}`,
      granularityLabel: this.translate.instant(
        EXPORT_GRANULARITIES.find(g => g.value === job.granularity)?.labelKey ?? '',
      ),
      dataLabel: this.translate.instant(
        PRESET_LABEL_KEYS[(job.energyPreset as EnergyPreset) ?? 'ALL'],
      ),
      createdAtLabel: this.formatDisplayDate(job.createdAt),
      statusLabel: this.translate.instant(HISTORY_STATUS_KEYS[job.status as ExportJobStatus]),
      statusState: HISTORY_STATUS_STATES[job.status as ExportJobStatus],
      downloadAvailable: job.status === 'completed',
      downloadUrl: job.downloadUrl || undefined,
      fileName: job.fileName,
    };
  }

  protected onHistoryRowDownload(row: ExportHistoryRow): void {
    if (!row.downloadAvailable) return;
    this.insightsService
      .downloadExportJob(row.exportJobOid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async response => {
        if (!response.body) return;
        // Server returns a gzipped CSV; decompress so the user gets a plain .csv file.
        // Prefer the LDN-date filename we build client-side; fall back to the server's name.
        const contentDisposition = response.headers.get('content-disposition') ?? '';
        const rawName = parseContentDispositionFilename(contentDisposition) || 'export.csv.gz';
        const filename = row.fileName || rawName.replace(/\.gz$/i, '');
        const csvBlob = await gunzipCsvUtil.gunzipToCsvBlob(response.body);
        gunzipCsvUtil.triggerBlobDownload(csvBlob, filename);
      });
  }
}
